import { useEffect, useState } from "react";
import { NameBasisBadge } from "../components/NameBasisBadge";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, ApiError, must } from "../api/client";
import { StampCard } from "../components/StampCard";
import { useWallet } from "../wallet/context";
import { shortDid } from "../util";
import { noteFromHash } from "../vouch/note";
import { delegationChoices, explain } from "../vouch/delegationOffer";
import { DidLabel } from "../components/DidLabel";
import { ArtifactPanel } from "../components/ArtifactPanel";

type Req = Awaited<ReturnType<typeof loadRequest>>;
const loadRequest = (token: string) => api.GET("/requests/{token}", { params: { path: { token } } });

/** /v/{token}: the vouch flow. Offers "vouch on behalf of <issuer>" only when this
 *  browser's wallet holds a valid AuthorisedAttester delegation for the claim's
 *  named issuer and claim type. */
export function VouchPage() {
  const { token = "" } = useParams();
  const nav = useNavigate();
  const { wallet, identity, ready } = useWallet();
  const [req, setReq] = useState<NonNullable<Req["data"]> | null>(null);
  const [gone, setGone] = useState("");
  const [bases, setBases] = useState<{ id: string; label: string }[]>([]);
  const [claimTypes, setClaimTypes] = useState<Record<string, string>>({});
  const [basis, setBasis] = useState("");
  const [relationship, setRelationship] = useState("");
  const [onBehalf, setOnBehalf] = useState<string>("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  // Read once from the fragment; kept in memory only, never sent anywhere.
  const [note] = useState(() => (typeof window !== "undefined" ? noteFromHash(window.location.hash) : ""));

  useEffect(() => {
    loadRequest(token).then((r) => {
      if (r.data) { setReq(r.data); setBasis(r.data.proposed_basis); setRelationship(r.data.proposed_relationship ?? ""); }
      else setGone(r.response.status === 410 ? "This vouch link has already been used or has expired." : "This vouch link does not exist.");
    });
    api.GET("/registry/bases").then((r) => setBases(r.data?.bases ?? []));
    api.GET("/registry/claim-types").then((r) => setClaimTypes(Object.fromEntries((r.data?.claim_types ?? []).map((t) => [t.id, t.label]))));
  }, [token]);

  if (gone) return <p className="note">{gone}</p>;
  if (!req || !ready) return <p className="note">Loading…</p>;

  const claimType = req.claim.claim.claim_type;
  // Delegations from the issuer this claim names: offered when one covers the claim now,
  // otherwise explained. Delegations from other issuers are never shown.
  const namedIssuer = req.claim.claim.issuer_did;
  const issuerName = req.claim.issuer?.name ?? req.delegable_issuers.find((i) => i.did === namedIssuer)?.name ?? "the issuer";
  const choices = delegationChoices(identity?.delegations ?? [], namedIssuer, claimType);
  const offers = req.delegable_issuers.filter((iss) => iss.did === namedIssuer)
    .flatMap((iss) => choices.offer.map((d) => ({ issuer: iss, delegation: d })));
  const notes = choices.notes.map((n) => explain(n, { issuer: issuerName, claimTypes }));

  const sign = async () => {
    setBusy(true); setErr("");
    try {
      const chosen = offers.find((o) => o.delegation.id === onBehalf);
      const subject: Record<string, unknown> = { id: `urn:qsv:claim:${req.claim.claim_id}`, decision: "signed", basis };
      if (relationship.trim()) subject.relationship = relationship.trim();
      if (chosen) subject.on_behalf_of = { issuer: chosen.issuer.did, delegation_id: chosen.delegation.id, delegation: chosen.delegation.credential };
      const entity = { "@context": ["https://www.w3.org/ns/credentials/v2"], type: ["VerifiableCredential", "VouchCredential"],
        issuer: identity!.did, attester_did: identity!.did, validFrom: new Date().toISOString(), credentialSubject: subject };
      const out = await must(api.POST("/requests/{token}/vouch", { params: { path: { token } },
        body: { jws: await wallet.signEnvelope("vouch", entity) } }));
      nav(`/v/${token}/done`, { state: out });
    } catch (e) {
      setErr(e instanceof ApiError && e.type?.includes("delegation_") ? `The delegation was refused: ${e.message}. You can still vouch in your own name.` : (e as Error).message);
    } finally { setBusy(false); }
  };

  return (
    <div>
      {/* Who is asking, and for what: shown first, before any picture is fetched or any key is used. */}
      <div className="card requester" data-testid="requester-card">
        <div data-testid="requester">{req.claim.holder_display_name
          ? <DidLabel did={req.claim.holder_did} name={req.claim.holder_display_name} />
          : <><span className="hint">no name set</span> · <span className="mono">{shortDid(req.claim.holder_did)}</span></>}</div>
        <div>asks you to vouch for a <b data-testid="requester-claim-type">{claimTypes[claimType] ?? claimType.replace(/[._]/g, " ")}</b></div>
      </div>
      <h1>Will you vouch for this?</h1>
      <p className="lead">Nothing is asked of you but a signature with your own key.</p>
      <p className="hint" data-testid="vouch-from">From <DidLabel did={req.claim.holder_did} name={req.claim.holder_display_name ?? null} /></p>
      {req.claim.claim.artifact_hash && Object.keys(claimTypes).length > 0 && <ArtifactPanel token={token} />}
      {note && (
        <div className="private-note" data-testid="private-note" role="note">
          <div className="private-note-label">Private note from the person who sent you this link. Not part of the public record.</div>
          <p data-testid="private-note-text">{note}</p>
        </div>
      )}
      <StampCard view={req.claim} compact />
      {!identity ? (
        <div className="card" style={{ marginTop: 16 }}>
          <p>To vouch you need a key in this browser.</p>
          <Link className="btn primary" to={`/me?return=/v/${token}`} data-testid="setup-keys">Set up your keys</Link>
        </div>
      ) : (
        <div style={{ marginTop: 8 }}>
          {offers.length > 0 && (<>
            <h2>Vouch as</h2>
            <label className={`choice${onBehalf === "" ? " on" : ""}`}>
              <input type="radio" name="as" checked={onBehalf === ""} onChange={() => setOnBehalf("")} />
              <span><b>Yourself</b><div className="hint">Your own vouch.</div></span>
            </label>
            {offers.map((o) => (
              <label key={o.delegation.id} className={`choice issuer${onBehalf === o.delegation.id ? " on" : ""}`} data-testid="on-behalf-option">
                <input type="radio" name="as" checked={onBehalf === o.delegation.id} onChange={() => setOnBehalf(o.delegation.id)} />
                <span><b>Vouch on behalf of <span data-testid="on-behalf-issuer">{o.issuer.name}</span></b> <NameBasisBadge basis={o.issuer.name_basis} domain={o.issuer.domain} />
                  <div className="hint">As {o.delegation.role}{o.delegation.name ? ` (${o.delegation.name})` : ""}. This anchors the claim.</div></span>
              </label>))}
          </>)}
          {offers.length === 0 && notes.map((t, i) => <p key={i} className="note" data-testid="delegation-note">{t}</p>)}
          <label htmlFor="basis">How do you know?</label>
          <select id="basis" value={basis} onChange={(e) => setBasis(e.target.value)} data-testid="basis">
            {bases.map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}
          </select>
          <label htmlFor="rel">Your relationship (optional)</label>
          <input id="rel" value={relationship} onChange={(e) => setRelationship(e.target.value)} placeholder="e.g. classmate, coach, reviewer" data-testid="relationship" />
          {err && <p className="error" role="alert" style={{ marginTop: 12 }}>{err}</p>}
          <div className="row" style={{ marginTop: 18 }}>
            <button className={onBehalf ? "issuer" : "primary"} disabled={busy || !basis} onClick={sign} data-testid="sign-vouch">
              {onBehalf ? `Sign on behalf of ${offers.find((o) => o.delegation.id === onBehalf)?.issuer.name}` : "Sign my vouch"}
            </button>
          </div>
          <p className="hint">Signed as <span className="mono">{shortDid(identity.did)}</span></p>
        </div>
      )}
    </div>
  );
}
